import React, { useEffect, useState, useRef, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/Button';
import { qualityCheckApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import {
  Play, Pause, SkipBack, SkipForward, Check, X, ChevronLeft, ChevronRight,
  Maximize, Volume2, VolumeX, Clock, Filter, RefreshCw, Download, ArrowLeft
} from 'lucide-react';

interface QCReview {
  id: string;
  media_file_id: string;
  file_request_id: string;
  editor_id: string;
  status: 'pending' | 'passed' | 'failed' | 'skipped' | 'pending_phase1' | 'pending_phase2' | 'failed_phase1' | 'failed_phase2';
  review_notes: string | null;
  queue_position: number;
  s3_url: string;
  thumbnail_url: string;
  original_filename: string;
  file_type: string;
  file_size: number;
  mime_type: string;
  editor_name: string;
  editor_code: string;
  request_title: string;
  request_type: string;
  request_vertical: string;
  buyer_name: string;
  buyer_email: string;
  reviewer_name: string;
  reviewed_at: string;
  created_at: string;
  phase1_reviewer_name?: string | null;
  phase1_reviewed_at?: string | null;
  phase1_notes?: string | null;
  phase2_reviewer_name?: string | null;
  phase2_reviewed_at?: string | null;
  phase2_notes?: string | null;
}

// Specific people granted full QC reviewer rights (both P1 and P2) without
// one of the gating roles. Kept in sync with backend qualityCheckController.js
// QC_REVIEWER_ALLOWED_EMAILS — update both lists together.
const QC_REVIEWER_ALLOWED_EMAILS = new Set<string>([
  'aditya.nawal@pearmediallc.com',
]);

export function QualityCheckPage() {
  const { user } = useAuth();
  const userRoles = [user?.role, ...((user as any)?.additional_roles || [])].filter(Boolean) as string[];
  const userEmail = ((user as any)?.email || '').toLowerCase();
  const isAllowlistedReviewer = QC_REVIEWER_ALLOWED_EMAILS.has(userEmail);
  const canPhase1 =
    userRoles.some(r => ['quality_check', 'creative_head', 'admin'].includes(r))
    || isAllowlistedReviewer;
  const canPhase2 =
    userRoles.some(r => ['creative_head', 'assistant_team_lead', 'admin'].includes(r))
    || isAllowlistedReviewer;
  const isAdmin = userRoles.includes('admin');

  const [reviews, setReviews] = useState<QCReview[]>([]);
  const [stats, setStats] = useState<any>({ phase1_pending: 0, phase2_pending: 0, passed: 0, failed_phase1: 0, failed_phase2: 0, skipped: 0 });
  const [loading, setLoading] = useState(true);
  // phase: '1' = Phase 1 queue (creative incharge), '2' = Phase 2 queue (final review)
  const [phase, setPhase] = useState<'1' | '2'>(canPhase2 && !canPhase1 ? '2' : '1');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const notesTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const selected = selectedIndex >= 0 ? reviews[selectedIndex] : null;

  const fetchData = useCallback(async () => {
    try {
      const [queueRes, statsRes] = await Promise.all([
        qualityCheckApi.getQueue({ phase, status: statusFilter, limit: 200 }),
        qualityCheckApi.getStats(),
      ]);
      setReviews(queueRes.data?.data?.reviews || []);
      setStats(statsRes.data?.data || {});
    } catch (err) {
      console.error('Failed to fetch QC data:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, phase]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh the queue every 15s when the tab is visible. This makes Phase 2
  // see new uploads/Phase-1-passes without requiring a manual refresh, and pauses
  // when the tab is hidden so we don't burn quota on backgrounded tabs.
  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null;
    const startPolling = () => {
      if (timer) return;
      timer = setInterval(() => {
        if (document.visibilityState === 'visible') fetchData();
      }, 15000);
    };
    const stopPolling = () => {
      if (timer) { clearInterval(timer); timer = null; }
    };
    if (document.visibilityState === 'visible') startPolling();
    const onVisChange = () => {
      if (document.visibilityState === 'visible') {
        fetchData();   // immediate refresh on tab focus
        startPolling();
      } else {
        stopPolling();
      }
    };
    document.addEventListener('visibilitychange', onVisChange);
    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', onVisChange);
    };
  }, [fetchData]);

  // When selection changes, load notes
  useEffect(() => {
    if (selected) setNotes(selected.review_notes || '');
  }, [selected?.id]);

  // Auto-save notes
  const handleNotesChange = (value: string) => {
    setNotes(value);
    if (notesTimer.current) clearTimeout(notesTimer.current);
    if (selected) {
      notesTimer.current = setTimeout(async () => {
        try { await qualityCheckApi.updateNotes(selected.id, value); } catch {}
      }, 1000);
    }
  };

  const handleReview = async (status: 'passed' | 'failed') => {
    if (!selected) return;
    setSaving(true);
    try {
      // Route to phase-specific endpoint based on the file's current status
      if (selected.status === 'pending_phase2') {
        await qualityCheckApi.phase2Review(selected.id, { status, review_notes: notes });
      } else {
        await qualityCheckApi.phase1Review(selected.id, { status, review_notes: notes });
      }
      const nextIdx = selectedIndex < reviews.length - 1 ? selectedIndex : selectedIndex - 1;
      await fetchData();
      setSelectedIndex(nextIdx >= 0 ? nextIdx : 0);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to submit review');
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = async (reviewId: string) => {
    if (!window.confirm('Send this review back to Phase 1? The file will be re-locked from buyer download and go back into the P1 queue.')) return;
    try {
      await qualityCheckApi.revertReview(reviewId);
      await fetchData();
      setSelectedIndex(-1);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to revert review');
    }
  };

  const handleSkip = async (reviewId: string) => {
    const reason = window.prompt('Reason for skipping (optional):');
    if (reason === null) return; // cancelled
    try {
      await qualityCheckApi.skipReview(reviewId, reason || undefined);
      const nextIdx = selectedIndex < reviews.length - 1 ? selectedIndex : selectedIndex - 1;
      await fetchData();
      setSelectedIndex(nextIdx >= 0 ? nextIdx : -1);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to skip review');
    }
  };

  const goTo = (idx: number) => {
    if (idx >= 0 && idx < reviews.length) setSelectedIndex(idx);
  };

  return (
    <DashboardLayout title="Quality Check">
      <div className="flex flex-col h-[calc(100vh-60px)]">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-card flex-shrink-0">
          <div className="flex items-center gap-4">
            {/* Phase tabs */}
            <div className="flex items-center gap-1 ml-2 border border-border rounded-lg p-0.5 bg-background">
              {canPhase1 && (
                <button
                  onClick={() => { setPhase('1'); setSelectedIndex(-1); setStatusFilter('pending'); }}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                    phase === '1' ? 'bg-blue-600 text-white' : 'text-muted-foreground hover:bg-muted'
                  }`}
                >
                  Phase 1 {stats.phase1_pending > 0 && <span className="ml-1 px-1 rounded bg-amber-500 text-white text-[9px]">{stats.phase1_pending}</span>}
                </button>
              )}
              {canPhase2 && (
                <button
                  onClick={() => { setPhase('2'); setSelectedIndex(-1); setStatusFilter('pending'); }}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                    phase === '2' ? 'bg-blue-600 text-white' : 'text-muted-foreground hover:bg-muted'
                  }`}
                >
                  Phase 2 (Final) {stats.phase2_pending > 0 && <span className="ml-1 px-1 rounded bg-amber-500 text-white text-[9px]">{stats.phase2_pending}</span>}
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex gap-1.5 text-xs">
              {phase === '1' ? (
                <>
                  <span className="px-2 py-1 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 font-medium">{stats.phase1_pending || 0} Pending P1</span>
                  <span className="px-2 py-1 rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 font-medium">{stats.failed_phase1 || 0} Failed P1</span>
                </>
              ) : (
                <>
                  <span className="px-2 py-1 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 font-medium">{stats.phase2_pending || 0} Pending P2</span>
                  <span className="px-2 py-1 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-medium">{stats.passed || 0} Passed (final)</span>
                </>
              )}
            </div>
            <select
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setSelectedIndex(-1); }}
              className="px-2 py-1.5 text-xs border border-border rounded bg-card text-foreground"
            >
              <option value="pending">Pending</option>
              <option value="passed">Passed</option>
              <option value="failed">Failed</option>
              <option value="skipped">Skipped</option>
              <option value="all">All</option>
            </select>
            <Button variant="outline" size="sm" onClick={() => { setLoading(true); fetchData(); }}>
              <RefreshCw size={13} className="mr-1" /> Refresh
            </Button>
          </div>
        </div>

        {/* Summary Table View (for passed/failed/all) */}
        {statusFilter !== 'pending' && selectedIndex < 0 ? (
          <div className="flex-1 overflow-auto bg-card">
            {loading ? (
              <div className="flex items-center justify-center p-8 text-muted-foreground text-sm">Loading reviews...</div>
            ) : reviews.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-12 text-muted-foreground text-sm">
                <Check size={32} className="mb-2 opacity-40" />
                <p>No {statusFilter} reviews</p>
              </div>
            ) : (
              <>
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-muted/80 backdrop-blur z-10">
                    <tr>
                      <th className="text-left p-3 font-semibold text-muted-foreground">#</th>
                      <th className="text-left p-3 font-semibold text-muted-foreground">File</th>
                      <th className="text-left p-3 font-semibold text-muted-foreground">Editor</th>
                      <th className="text-left p-3 font-semibold text-muted-foreground">Buyer</th>
                      <th className="text-left p-3 font-semibold text-muted-foreground">Vertical</th>
                      <th className="text-left p-3 font-semibold text-muted-foreground">Request</th>
                      <th className="text-left p-3 font-semibold text-muted-foreground">Status</th>
                      <th className="text-left p-3 font-semibold text-muted-foreground">Reviewed By</th>
                      <th className="text-left p-3 font-semibold text-muted-foreground">Reviewed At</th>
                      <th className="text-left p-3 font-semibold text-muted-foreground min-w-[200px]">Notes</th>
                      <th className="text-left p-3 font-semibold text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reviews.map((r, i) => (
                      <tr
                        key={r.id}
                        onClick={() => setSelectedIndex(i)}
                        className="cursor-pointer border-b border-border/30 transition-colors hover:bg-muted/50"
                      >
                        <td className="p-3 text-muted-foreground">{i + 1}</td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <Play size={12} className="text-blue-500 flex-shrink-0" />
                            <span className="truncate max-w-[250px] font-medium" title={r.original_filename}>{r.original_filename}</span>
                          </div>
                        </td>
                        <td className="p-3 text-muted-foreground">{r.editor_name || r.editor_code || '—'}</td>
                        <td className="p-3 text-muted-foreground">{r.buyer_name || '—'}</td>
                        <td className="p-3 text-muted-foreground">{r.request_vertical || '—'}</td>
                        <td className="p-3 text-muted-foreground truncate max-w-[120px]" title={r.request_title}>{r.request_title || '—'}</td>
                        <td className="p-3">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded whitespace-nowrap ${
                            r.status === 'passed' ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400' :
                            (r.status === 'failed' || r.status === 'failed_phase1' || r.status === 'failed_phase2') ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400' :
                            r.status === 'skipped' ? 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' :
                            r.status === 'pending_phase2' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400' :
                            'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400'
                          }`}>{
                            r.status === 'pending_phase1' ? 'P1 PENDING' :
                            r.status === 'pending_phase2' ? 'P1 PASSED → P2' :
                            r.status === 'failed_phase1' ? 'P1 FAILED' :
                            r.status === 'failed_phase2' ? 'P2 FAILED' :
                            r.status.toUpperCase()
                          }</span>
                        </td>
                        <td className="p-3 text-muted-foreground">
                          {phase === '2' ? (r.phase2_reviewer_name || r.phase1_reviewer_name || r.reviewer_name || '—') : (r.phase1_reviewer_name || r.reviewer_name || '—')}
                        </td>
                        <td className="p-3 text-muted-foreground whitespace-nowrap">
                          {r.reviewed_at ? new Date(r.reviewed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' ' + new Date(r.reviewed_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '—'}
                        </td>
                        <td className="p-3 text-muted-foreground">
                          <span className="line-clamp-2" title={r.review_notes || ''}>{r.review_notes || '—'}</span>
                        </td>
                        <td className="p-3">
                          <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                            {r.status === 'passed' && (canPhase2 || isAdmin) && (
                              <button
                                onClick={() => handleRevert(r.id)}
                                className="px-2 py-1 text-[10px] font-medium rounded border border-orange-300 text-orange-700 hover:bg-orange-50 dark:border-orange-600 dark:text-orange-400 dark:hover:bg-orange-900/20 whitespace-nowrap"
                              >
                                Retract → P1
                              </button>
                            )}
                            {isAdmin && ['failed', 'failed_phase1', 'failed_phase2'].includes(r.status) && (
                              <button
                                onClick={() => handleRevert(r.id)}
                                className="px-2 py-1 text-[10px] font-medium rounded border border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-600 dark:text-amber-400 dark:hover:bg-amber-900/20 whitespace-nowrap"
                              >
                                Reset to P1
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/30 text-xs text-muted-foreground">
                  <span>{reviews.length} review{reviews.length !== 1 ? 's' : ''}</span>
                  <button
                    onClick={() => {
                      const headers = ['#', 'File', 'Editor', 'Buyer', 'Vertical', 'Request', 'Status', 'Reviewed By', 'Reviewed At', 'Notes'];
                      const rows = reviews.map((r, i) => [
                        i + 1, r.original_filename, r.editor_name || r.editor_code || '', r.buyer_name || '',
                        r.request_vertical || '', r.request_title || '', r.status,
                        r.reviewer_name || '', r.reviewed_at ? new Date(r.reviewed_at).toISOString() : '',
                        (r.review_notes || '').replace(/"/g, '""')
                      ]);
                      const csv = [headers.join(','), ...rows.map(row => row.map(c => `"${c}"`).join(','))].join('\n');
                      const blob = new Blob([csv], { type: 'text/csv' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a'); a.href = url; a.download = `qc-${statusFilter}-${new Date().toISOString().split('T')[0]}.csv`; a.click();
                      URL.revokeObjectURL(url);
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-border hover:bg-muted transition-colors"
                  >
                    <Download size={12} /> Export CSV
                  </button>
                </div>
              </>
            )}
          </div>
        ) : (
        <div className="flex flex-1 overflow-hidden">
          {/* Queue Table (left) — shown in review mode or when drilled in from summary */}
          <div className="w-[420px] flex-shrink-0 border-r border-border overflow-y-auto bg-card">
            {statusFilter !== 'pending' && selectedIndex >= 0 && (
              <button
                onClick={() => setSelectedIndex(-1)}
                className="flex items-center gap-1.5 px-3 py-2 text-xs text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 w-full border-b border-border"
              >
                <ArrowLeft size={12} /> Back to summary
              </button>
            )}
            {loading ? (
              <div className="flex items-center justify-center p-8 text-muted-foreground text-sm">Loading queue...</div>
            ) : reviews.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-8 text-muted-foreground text-sm">
                <Check size={32} className="mb-2 opacity-40" />
                <p>No {statusFilter} reviews</p>
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-muted/80 backdrop-blur z-10">
                  <tr>
                    <th className="text-left p-2 font-semibold text-muted-foreground">#</th>
                    <th className="text-left p-2 font-semibold text-muted-foreground">Video</th>
                    <th className="text-left p-2 font-semibold text-muted-foreground">Editor</th>
                    <th className="text-left p-2 font-semibold text-muted-foreground">Buyer</th>
                    <th className="text-left p-2 font-semibold text-muted-foreground">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reviews.map((r, i) => (
                    <tr
                      key={r.id}
                      onClick={() => setSelectedIndex(i)}
                      className={`cursor-pointer border-b border-border/30 transition-colors ${
                        selectedIndex === i ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-muted/50'
                      }`}
                    >
                      <td className="p-2 text-muted-foreground">{r.queue_position}</td>
                      <td className="p-2">
                        <div className="flex items-center gap-2">
                          <Play size={12} className="text-blue-500 flex-shrink-0" />
                          <span className="truncate max-w-[140px]" title={r.original_filename}>{r.original_filename}</span>
                        </div>
                      </td>
                      <td className="p-2 text-muted-foreground truncate max-w-[80px]">{r.editor_name || r.editor_code || '—'}</td>
                      <td className="p-2 text-muted-foreground truncate max-w-[80px]">{r.buyer_name || '—'}</td>
                      <td className="p-2">
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap ${
                          r.status === 'passed' ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400' :
                          (r.status === 'failed' || r.status === 'failed_phase1' || r.status === 'failed_phase2') ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400' :
                          r.status === 'pending_phase2' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400' :
                          'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400'
                        }`}>{
                          r.status === 'pending_phase1' ? 'P1' :
                          r.status === 'pending_phase2' ? 'P2' :
                          r.status === 'failed_phase1' ? 'P1 FAIL' :
                          r.status === 'failed_phase2' ? 'P2 FAIL' :
                          r.status.toUpperCase()
                        }</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Video Player + Review Panel (right) */}
          <div className="flex-1 flex flex-col overflow-hidden bg-background">
            {!selected ? (
              <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
                <div className="text-center">
                  <Play size={48} className="mx-auto mb-3 opacity-20" />
                  <p>Select a video from the queue to review</p>
                </div>
              </div>
            ) : (
              <>
                {/* Nav bar */}
                <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-card flex-shrink-0">
                  <button onClick={() => goTo(selectedIndex - 1)} disabled={selectedIndex <= 0} className="p-1.5 rounded hover:bg-muted disabled:opacity-30">
                    <ChevronLeft size={16} />
                  </button>
                  <div className="text-xs text-center">
                    <span className="font-medium">{selected.original_filename}</span>
                    <span className="text-muted-foreground ml-2">{selectedIndex + 1} of {reviews.length}</span>
                  </div>
                  <button onClick={() => goTo(selectedIndex + 1)} disabled={selectedIndex >= reviews.length - 1} className="p-1.5 rounded hover:bg-muted disabled:opacity-30">
                    <ChevronRight size={16} />
                  </button>
                </div>

                {/* Media + Info */}
                <div className="flex-1 flex overflow-hidden">
                  {/* Media preview (center) — image or video based on file_type */}
                  <div className="flex-1 flex items-center justify-center bg-black p-2">
                    {(selected.file_type === 'image' || (selected.mime_type && selected.mime_type.startsWith('image/'))) ? (
                      <ImageViewer
                        key={selected.media_file_id}
                        src={selected.s3_url}
                        alt={selected.original_filename}
                        onNext={() => goTo(selectedIndex + 1)}
                        onPrev={() => goTo(selectedIndex - 1)}
                      />
                    ) : (
                      <VideoPlayer
                        key={selected.media_file_id}     /* React unmounts/remounts only when file changes */
                        mediaFileId={selected.media_file_id}
                        src={selected.s3_url}
                        poster={selected.thumbnail_url}
                        onNext={() => goTo(selectedIndex + 1)}
                        onPrev={() => goTo(selectedIndex - 1)}
                      />
                    )}
                  </div>

                  {/* Review sidebar */}
                  <div className="w-[320px] flex-shrink-0 border-l border-border flex flex-col bg-card overflow-y-auto">
                    {/* Info */}
                    <div className="p-3 border-b border-border space-y-2 text-xs">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Editor</span>
                        <span className="font-medium">{selected.editor_name || selected.editor_code || '—'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Requested by</span>
                        <span className="font-medium">{selected.buyer_name || '—'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Vertical</span>
                        <span className="font-medium">{selected.request_vertical || '—'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Request</span>
                        <span className="font-medium truncate max-w-[160px]">{selected.request_title || '—'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">File</span>
                        <span className="font-medium text-[10px] truncate max-w-[160px]">{selected.original_filename}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Size</span>
                        <span className="font-medium">{(selected.file_size / (1024 * 1024)).toFixed(1)} MB</span>
                      </div>
                      {selected.phase1_reviewer_name && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Phase 1 by</span>
                          <span className="font-medium">{selected.phase1_reviewer_name}</span>
                        </div>
                      )}
                      {selected.phase2_reviewer_name && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Phase 2 by</span>
                          <span className="font-medium">{selected.phase2_reviewer_name}</span>
                        </div>
                      )}
                      {selected.reviewed_at && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Last reviewed</span>
                          <span className="font-medium">{new Date(selected.reviewed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} {new Date(selected.reviewed_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      )}
                    </div>

                    {/* Phase 1 notes box (visible when in Phase 2 review or after P1 was done) */}
                    {selected.phase1_notes && (selected.status === 'pending_phase2' || selected.phase1_reviewed_at) && (
                      <div className="p-3 border-b border-border bg-blue-50 dark:bg-blue-900/10">
                        <label className="text-xs font-semibold text-blue-700 dark:text-blue-400 block mb-1">Phase 1 Notes (read-only)</label>
                        <p className="text-xs text-foreground whitespace-pre-wrap">{selected.phase1_notes}</p>
                      </div>
                    )}

                    {/* Phase 2 feedback (visible when P2 sent it back, so P1 sees what to recheck) */}
                    {selected.phase2_notes && selected.status === 'pending_phase1' && selected.phase2_reviewed_at && (
                      <div className="p-3 border-b border-border bg-orange-50 dark:bg-orange-900/10">
                        <label className="text-xs font-semibold text-orange-700 dark:text-orange-400 block mb-1">⚠ Sent back from Phase 2 — please recheck</label>
                        <p className="text-xs text-foreground whitespace-pre-wrap">{selected.phase2_notes}</p>
                      </div>
                    )}

                    {/* Notes */}
                    <div className="p-3 flex-1 flex flex-col">
                      <label className="text-xs font-semibold text-muted-foreground mb-1.5">
                        {selected.status === 'pending_phase2' ? 'Phase 2 Review Notes' : selected.status === 'pending_phase1' ? 'Phase 1 Review Notes' : 'Review Notes'}
                      </label>
                      <textarea
                        value={notes}
                        onChange={e => handleNotesChange(e.target.value)}
                        placeholder="Type review notes here... (auto-saves)"
                        className="flex-1 min-h-[100px] p-2.5 text-sm border border-border rounded-lg bg-background text-foreground resize-none outline-none focus:border-primary"
                        readOnly={!['pending', 'pending_phase1', 'pending_phase2'].includes(selected.status)}
                      />
                    </div>

                    {/* Actions */}
                    <div className="p-3 border-t border-border space-y-2">
                      {(selected.status === 'pending' || selected.status === 'pending_phase1' || selected.status === 'pending_phase2') && (
                        <>
                          <div className="flex gap-2">
                            <Button
                              onClick={() => handleReview('failed')}
                              disabled={saving}
                              variant="destructive"
                              className="flex-1"
                            >
                              <X size={14} className="mr-1.5" />
                              {selected.status === 'pending_phase2' ? 'Send back to P1' : 'Fail (back to editor)'}
                            </Button>
                            <Button
                              onClick={() => handleReview('passed')}
                              disabled={saving}
                              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                            >
                              <Check size={14} className="mr-1.5" />
                              {selected.status === 'pending_phase2' ? 'Final Pass' : 'Pass to P2'}
                            </Button>
                          </div>
                          <button
                            onClick={() => handleSkip(selected.id)}
                            className="w-full text-xs text-muted-foreground hover:text-foreground py-1.5 border border-border rounded hover:bg-muted transition-colors"
                          >
                            Skip / Remove from queue
                          </button>
                        </>
                      )}
                      {/* Phase 2 reviewers can retract a 'passed' review */}
                      {selected.status === 'passed' && (canPhase2 || isAdmin) && (
                        <button
                          onClick={() => handleRevert(selected.id)}
                          className="w-full flex items-center justify-center gap-1.5 text-xs font-medium text-orange-700 dark:text-orange-400 py-2 border border-orange-300 dark:border-orange-600 rounded hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors"
                        >
                          <RefreshCw size={12} /> Retract pass / Send back to Phase 1
                        </button>
                      )}
                      {/* Admin-only full reset for failed/non-passed reviews */}
                      {(selected.status === 'failed' || selected.status === 'failed_phase1' || selected.status === 'failed_phase2') && isAdmin && (
                        <button
                          onClick={() => handleRevert(selected.id)}
                          className="w-full flex items-center justify-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400 py-2 border border-amber-300 dark:border-amber-600 rounded hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
                        >
                          <RefreshCw size={12} /> Reset to Phase 1 (admin only)
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        )}

      </div>
    </DashboardLayout>
  );
}

// ── Image Viewer Component ──
// Static image preview with zoom toggle + nav keyboard shortcuts (n/N, p/P, f for fullscreen)

interface ImageViewerProps {
  src: string;
  alt: string;
  onNext: () => void;
  onPrev: () => void;
}

function ImageViewer({ src, alt, onNext, onPrev }: ImageViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.tagName === 'TEXTAREA' || (e.target as HTMLElement)?.tagName === 'INPUT') return;
      switch (e.key) {
        case 'n': case 'N': e.preventDefault(); onNext(); break;
        case 'p': case 'P': e.preventDefault(); onPrev(); break;
        case 'f': e.preventDefault();
          if (!containerRef.current) return;
          if (document.fullscreenElement) document.exitFullscreen();
          else containerRef.current.requestFullscreen();
          break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onNext, onPrev]);

  return (
    <div ref={containerRef} className="relative flex items-center justify-center h-full w-full">
      <img
        src={src}
        alt={alt}
        onClick={() => setZoomed(z => !z)}
        className={`rounded-lg object-contain cursor-pointer transition-transform ${zoomed ? 'max-w-none max-h-none scale-150' : 'max-h-full max-w-full'}`}
      />
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-2">
        <button onClick={onPrev} className="p-1.5 bg-black/60 text-white/80 hover:text-white rounded" title="Previous (P)">
          <ChevronLeft size={14} />
        </button>
        <button onClick={() => setZoomed(z => !z)} className="px-2 py-1 bg-black/60 text-white/80 hover:text-white rounded text-[10px] font-medium">
          {zoomed ? 'Fit' : 'Zoom'}
        </button>
        <button onClick={onNext} className="p-1.5 bg-black/60 text-white/80 hover:text-white rounded" title="Next (N)">
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}

// ── Video Player Component ──
// Optimized for vertical videos. Keyboard shortcuts: Space, J, K, L, arrows, < >

interface VideoPlayerProps {
  /** Stable identity. The component re-loads only when this changes (NOT when src changes).
   *  This is critical because src is a freshly-signed proxy URL that gets regenerated on every
   *  QC queue poll (15s) — without this guard the video reset to start every 15 seconds. */
  mediaFileId: string;
  src: string;
  poster?: string;
  onNext: () => void;
  onPrev: () => void;
}

function VideoPlayer({ mediaFileId, src, poster, onNext, onPrev }: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  // Hold the very first src value so a re-render with a refreshed token doesn't
  // change the <video src=...> attribute (which would force the browser to re-fetch
  // and reset playback). The token in `src` may rotate on every queue poll, but as
  // long as it points at the same file, we keep using the original signed URL —
  // the backend's token TTL is generous enough for a full playback session.
  const initialSrcRef = useRef(src);
  // Reset the cached src ONLY when the underlying file changes (parent's `key` also
  // remounts the whole component, so this is belt-and-suspenders).
  useEffect(() => {
    initialSrcRef.current = src;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaFileId]);
  const stableSrc = initialSrcRef.current;

  const speeds = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4];

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    v.currentTime = 0;
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    v.load();
    // Only react to file changes, NEVER to src token rotation
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaFileId]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => setCurrentTime(v.currentTime);
    const onDur = () => setDuration(v.duration || 0);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    v.addEventListener('timeupdate', onTime);
    v.addEventListener('loadedmetadata', onDur);
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    return () => {
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('loadedmetadata', onDur);
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaFileId]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const v = videoRef.current;
      if (!v) return;
      // Don't trigger if typing in textarea/input
      if ((e.target as HTMLElement)?.tagName === 'TEXTAREA' || (e.target as HTMLElement)?.tagName === 'INPUT') return;

      switch (e.key) {
        case ' ': case 'k': e.preventDefault(); v.paused ? v.play() : v.pause(); break;
        case 'j': e.preventDefault(); v.currentTime = Math.max(0, v.currentTime - 10); break;
        case 'l': e.preventDefault(); v.currentTime = Math.min(v.duration, v.currentTime + 10); break;
        case 'ArrowLeft': e.preventDefault(); v.currentTime = Math.max(0, v.currentTime - 5); break;
        case 'ArrowRight': e.preventDefault(); v.currentTime = Math.min(v.duration, v.currentTime + 5); break;
        case 'ArrowUp': e.preventDefault(); v.volume = Math.min(1, v.volume + 0.1); setVolume(v.volume); break;
        case 'ArrowDown': e.preventDefault(); v.volume = Math.max(0, v.volume - 0.1); setVolume(v.volume); break;
        case ',': e.preventDefault(); if (v.paused) v.currentTime = Math.max(0, v.currentTime - 1/30); break; // frame back
        case '.': e.preventDefault(); if (v.paused) v.currentTime = Math.min(v.duration, v.currentTime + 1/30); break; // frame forward
        case '<': e.preventDefault(); cycleSpeed(-1); break;
        case '>': e.preventDefault(); cycleSpeed(1); break;
        case 'f': e.preventDefault(); toggleFullscreen(); break;
        case 'm': e.preventDefault(); v.muted = !v.muted; setMuted(v.muted); break;
        case 'n': case 'N': e.preventDefault(); onNext(); break;
        case 'p': case 'P': e.preventDefault(); onPrev(); break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [speed, onNext, onPrev]);

  const cycleSpeed = (dir: number) => {
    const idx = speeds.indexOf(speed);
    const next = idx + dir;
    if (next >= 0 && next < speeds.length) {
      const newSpeed = speeds[next];
      setSpeed(newSpeed);
      if (videoRef.current) videoRef.current.playbackRate = newSpeed;
    }
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else containerRef.current.requestFullscreen();
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    v.paused ? v.play() : v.pause();
  };

  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    const v = videoRef.current;
    if (!v || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    v.currentTime = pct * duration;
  };

  const fmtTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div ref={containerRef} className="relative flex flex-col items-center justify-center h-full w-full max-w-[480px]">
      {/* Video — vertical aspect ratio optimized */}
      <video
        ref={videoRef}
        src={stableSrc}
        poster={poster}
        className="max-h-full max-w-full rounded-lg object-contain"
        playsInline
        onClick={togglePlay}
        style={{ aspectRatio: '9/16', maxHeight: 'calc(100% - 64px)' }}
      />

      {/* Controls bar */}
      <div className="w-full mt-2 px-1">
        {/* Progress bar */}
        <div className="w-full h-1.5 bg-gray-700 rounded cursor-pointer mb-2" onClick={seek}>
          <div className="h-full bg-blue-500 rounded" style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }} />
        </div>

        <div className="flex items-center justify-between gap-2">
          {/* Left controls */}
          <div className="flex items-center gap-1">
            <button onClick={() => { if (videoRef.current) videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 10); }} className="p-1 text-white/70 hover:text-white" title="Rewind 10s (J)">
              <SkipBack size={14} />
            </button>
            <button onClick={togglePlay} className="p-1.5 text-white hover:text-blue-400" title="Play/Pause (Space/K)">
              {playing ? <Pause size={18} /> : <Play size={18} />}
            </button>
            <button onClick={() => { if (videoRef.current) videoRef.current.currentTime = Math.min(duration, videoRef.current.currentTime + 10); }} className="p-1 text-white/70 hover:text-white" title="Forward 10s (L)">
              <SkipForward size={14} />
            </button>
          </div>

          {/* Time */}
          <span className="text-[10px] text-white/60 font-mono">{fmtTime(currentTime)} / {fmtTime(duration)}</span>

          {/* Right controls */}
          <div className="flex items-center gap-1">
            {/* Speed */}
            <button onClick={() => cycleSpeed(1)} className="px-1.5 py-0.5 text-[10px] font-bold text-white/70 hover:text-white bg-white/10 rounded" title="Speed (< >)">
              {speed}x
            </button>
            {/* Volume */}
            <button onClick={() => { if (videoRef.current) { videoRef.current.muted = !videoRef.current.muted; setMuted(videoRef.current.muted); }}} className="p-1 text-white/70 hover:text-white" title="Mute (M)">
              {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
            </button>
            {/* Fullscreen */}
            <button onClick={toggleFullscreen} className="p-1 text-white/70 hover:text-white" title="Fullscreen (F)">
              <Maximize size={14} />
            </button>
            {/* Nav */}
            <button onClick={onPrev} className="p-1 text-white/70 hover:text-white" title="Previous (P)">
              <ChevronLeft size={14} />
            </button>
            <button onClick={onNext} className="p-1 text-white/70 hover:text-white" title="Next (N)">
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
